import ScheduledMeetingActions from "../ScheduledMeetingActions";
import ActivityGroups from "../ActivityGroups";
// src/components/CandidatesTabsContent/CandidateActivitiesTabContent.jsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Divider,
  Chip,
} from "@mui/material";

import FigmaIcon from "../icons/FigmaIcon";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { EmailOutlined as EmailOutlinedIcon } from "../icons/FigmaControlIcons";
import { BoltOutlined as BoltOutlinedIcon } from "../icons/FigmaControlIcons";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import {
  activityToolbarRowSx,
  activityToolbarDropdownGroupSx,
  activityToolbarMenuItemSx,
  activityToolbarSelectSx,
  activityToolbarTextFieldSx,
} from "../activityToolbarStyles";
import { toTimeValue } from "../../utils/dateTime";

const BORDER = "#E5E7EB";
const PURPLE = "#7C3AED";
const ACTIVITY_HEADING_SX = {
  color: "#3451F4",
  fontSize: 16,
  fontWeight: 700,
  lineHeight: "24px",
};
const ACTIVITY_PARAGRAPH_SX = {
  fontSize: 14,
  fontWeight: 400,
};

function TopSelect({ value, onChange, items }) {
  return (
    <FormControl size="small" sx={{ width: { xs: "100%", md: 324 }, maxWidth: "100%" }}>
      <Select
        value={value}
        onChange={onChange}
        IconComponent={KeyboardArrowDownRoundedIcon}
        sx={activityToolbarSelectSx}
      >
        {items.map((opt) => (
          <MenuItem key={opt.value} value={opt.value} sx={activityToolbarMenuItemSx}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function TypeIcon({ type }) {
  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        display: "grid",
        placeItems: "center",
        flex: "0 0 auto",
      }}
    >
      {type === "email" && <EmailOutlinedIcon sx={{ width: 24, height: 24, color: "#3451F4" }} />}
      {type === "note" && <NoteAltOutlinedIcon sx={{ width: 24, height: 24, color: "#3451F4" }} />}
      {type !== "email" && type !== "note" && <BoltOutlinedIcon sx={{ width: 24, height: 24, color: "#3451F4" }} />}
    </Box>
  );
}

function ActionLink({ children, onClick }) {
  return (
    <Typography
      onClick={onClick}
      sx={{
        fontSize: 11,
        fontWeight: 700,
        color: PURPLE,
        cursor: "pointer",
        userSelect: "none",
        "&:hover": { textDecoration: "underline" },
      }}
    >
      {children}
    </Typography>
  );
}

function BodyWithMentions({ text }) {
  if (!text) return null;
  // Use a simple capturing-group split — no lookahead, no \s in mention, no undefined entries
  const parts = String(text).split(/(@[A-Za-z]\w*)/);
  return (
    <Typography sx={{ mt: 0.75, ...ACTIVITY_PARAGRAPH_SX, color: "#334155", lineHeight: 1.6, whiteSpace: "pre-line" }}>
      {parts.map((part, i) =>
        part && part.startsWith("@") ? (
          <Box key={i} component="span" sx={{ color: "#3451F4", fontWeight: 600 }}>
            {part}
          </Box>
        ) : (
          part || ""
        )
      )}
    </Typography>
  );
}

function ActivityItem({ item, onReadMore, onDelete, onEdit }) {
  const dateText = item?.dateText || "";

  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
        <TypeIcon type={item.type} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Title row */}
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
            <Typography sx={ACTIVITY_HEADING_SX}>
              {item.title}
            </Typography>

            {!!dateText && (
              <Typography sx={{ fontSize: 14, color: "#94A3B8", whiteSpace: "nowrap" }}>
                {dateText}
              </Typography>
            )}
          </Box>

          {/* Meta (like From / To / Subject, or Added by) */}
          {item.meta?.length ? (
            <Box sx={{ mt: 0.5 }}>
              {item.meta.map((m, idx) => (
                <Typography key={idx} sx={{ fontSize: 11, fontWeight: 700, color: "#98A2B3", lineHeight: 1.5 }}>
                  {m.label ? (
                    <>
                      <Box component="span" sx={{ fontWeight: 600, color: "#6B7280" }}>
                        {m.label}{" "}
                      </Box>
                      {m.value}
                    </>
                  ) : (
                    m.value
                  )}
                </Typography>
              ))}
            </Box>
          ) : null}

          {/* Body */}
          {item.body ? <BodyWithMentions text={item.body} /> : null}

          {/* Attachments */}
          {item.attachments?.length ? (
            <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
              {item.attachments.map((a, idx) => (
                <Chip
                  key={idx}
                  icon={<AttachFileRoundedIcon sx={{ fontSize: 16 }} />}
                  label={a.label}
                  onClick={() => a.onClick?.()}
                  sx={{
                    height: 26,
                    borderRadius: 999,
                    bgcolor: "#fff",
                    border: `1px solid ${BORDER}`,
                    fontSize: 11,
                    fontWeight: 800,
                    color: PURPLE,
                    "& .MuiChip-icon": { color: PURPLE },
                    cursor: a.onClick ? "pointer" : "default",
                  }}
                  variant="outlined"
                />
              ))}
            </Box>
          ) : null}

          {/* Footer links */}
          {item.raw?.metadata?.calendar_event_id && <ScheduledMeetingActions record={{ ...item.raw.metadata, id: item.raw.id, actor_user_id: item.raw.actor_user_id }} />}
          {(!item.raw?.metadata?.calendar_event_id && (item.showReadMore || item.showEdit || item.showDelete)) && (
            <Box sx={{ mt: 1, display: "flex", gap: 2, alignItems: "center" }}>
              {item.showReadMore && <ActionLink onClick={() => onReadMore?.(item)}>Read More</ActionLink>}
              {item.showEdit && <ActionLink onClick={() => onEdit?.(item)}>Edit</ActionLink>}
              {item.showDelete && <ActionLink onClick={() => onDelete?.(item)}>Delete</ActionLink>}
            </Box>
          )}
        </Box>
      </Box>


    </Box>
  );
}

export default function CandidateActivitiesTabContent({
  activities,
  onReadMore,
  onDelete,
  onEdit,
}) {
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");

  const typeOptions = useMemo(
    () => [
      { value: "all", label: "All Activities" },
      { value: "email", label: "Emails" },
      { value: "action", label: "Actions" },
      { value: "note", label: "Notes" },
    ],
    []
  );

  const sortOptions = useMemo(
    () => [
      { value: "newest", label: "Sort by" },
      { value: "oldest", label: "Oldest" },
      { value: "az", label: "Title (A-Z)" },
      { value: "za", label: "Title (Z-A)" },
    ],
    []
  );

  const filtered = useMemo(() => {
    const data = Array.isArray(activities) ? activities : [];
    let out = data;

    if (typeFilter !== "all") out = out.filter((x) => x.type === typeFilter);

    const query = q.trim().toLowerCase();
    if (query) {
      out = out.filter((x) => {
        const blob = [
          x.title,
          x.body,
          ...(x.meta || []).map((m) => `${m.label || ""} ${m.value || ""}`),
          ...(x.attachments || []).map((a) => a.label || ""),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return blob.includes(query);
      });
    }

    // sort
    const arr = [...out];
    if (sortBy === "oldest" || sortBy === "newest") {
      arr.sort((a, b) => {
        const ta = toTimeValue(a?.ts);
        const tb = toTimeValue(b?.ts);
        return sortBy === "oldest" ? ta - tb : tb - ta;
      });
    } else if (sortBy === "az" || sortBy === "za") {
      arr.sort((a, b) => {
        const aa = String(a.title || "").toLowerCase();
        const bb = String(b.title || "").toLowerCase();
        if (aa < bb) return sortBy === "az" ? -1 : 1;
        if (aa > bb) return sortBy === "az" ? 1 : -1;
        return 0;
      });
    }

    return arr;
  }, [activities, q, typeFilter, sortBy]);


  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: 2,
       
        overflow: "hidden",
      }}
    >
      {/* Top controls */}
      <Box
        sx={{
          px: 0,
          py: 1.25,
          display: "flex",
          alignItems: "center",
          ...activityToolbarRowSx,
          bgcolor: "#fff",
        }}
      >
        <TextField
          size="small"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search..."
          sx={activityToolbarTextFieldSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FigmaIcon name="search" color="#9C4FFF" />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={activityToolbarDropdownGroupSx}>
          <TopSelect value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} items={typeOptions} />
          <TopSelect value={sortBy} onChange={(e) => setSortBy(e.target.value)} items={sortOptions} />
        </Box>
      </Box>

      <Divider sx={{ borderColor: "#EEF2F7" }} />

      <ActivityGroups items={filtered} typeFilter={typeFilter} resetKey={JSON.stringify([q, typeFilter, sortBy])}
        renderItem={item => <ActivityItem item={item} onReadMore={onReadMore} onDelete={onDelete} onEdit={onEdit} />} />
    </Box>
  );
}
